import type { MouseEvent, ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShowSummary, ShowViewerRole, SpendWalletBalance } from '@fetmode/types';
import {
  SHOW_PUBLIC_RECORDING_COINS,
  SHOW_SNAPSHOT_COINS,
  isPublicCaptureShowType,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useAuth } from '../auth/index.js';
import { blobToDataUrl, captureVideoJpeg, pickRecorderMimeType, RECORDING_MAX_MS } from './show-capture.js';

export interface ShowRoomCaptureBarProps {
  show: ShowSummary;
  viewerRole: ShowViewerRole | null;
  remoteStream: MediaStream | null;
  videoEl: HTMLVideoElement | null;
  c2cOn: boolean;
  c2cBusy: boolean;
  onC2cToggle: () => void;
  onWallet: (wallet: SpendWalletBalance) => void;
  requireSpendCoins: (needed: number, onReady?: () => void | Promise<void>) => Promise<boolean>;
  requireAuth: (action: () => void, intent?: 'wallet' | 'purchase' | 'generic') => boolean;
  /** Portal target inside the room More menu (phone overflow). */
  menuSlot?: HTMLElement | null;
}

export function ShowRoomCaptureBar({
  show,
  viewerRole,
  remoteStream,
  videoEl,
  c2cOn,
  c2cBusy,
  onC2cToggle,
  onWallet,
  requireSpendCoins,
  requireAuth,
  menuSlot = null,
}: ShowRoomCaptureBarProps): ReactElement | null {
  const { api } = useAuth();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingShow, setSavingShow] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const stopTimerRef = useRef<number | null>(null);
  const uploadingRef = useRef(false);

  const live = show.status === 'LIVE' || show.status === 'PAUSED' || show.status === 'AWAY';
  const publicCapture = live && isPublicCaptureShowType(show.type) && Boolean(show.isViewer);
  const exclusiveC2c = live && show.type === 'EXCLUSIVE';
  const inExclusive = viewerRole === 'PARTICIPANT';

  const clearNotice = useCallback(() => {
    setMessage(null);
    setError(null);
  }, []);

  const uploadRecording = useCallback(async () => {
    if (uploadingRef.current) {
      return;
    }
    uploadingRef.current = true;
    setRecordingBusy(true);
    const blob = new Blob(chunksRef.current, { type: 'video/webm' });
    chunksRef.current = [];
    const durationSeconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
    try {
      if (blob.size < 32) {
        setError(t('common.show.recordError'));
        return;
      }
      const videoDataUrl = await blobToDataUrl(blob);
      const result = await api.captureShowRecording(show.id, { videoDataUrl, durationSeconds });
      onWallet(result.wallet);
      setMessage(t('common.show.recordSaved'));
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'INSUFFICIENT_BALANCE')) {
        setError(t('common.show.recordError'));
      }
    } finally {
      uploadingRef.current = false;
      setRecordingBusy(false);
      setRecording(false);
    }
  }, [api, onWallet, show.id]);

  const stopRecorder = useCallback(
    (upload: boolean) => {
      const recorder = recorderRef.current;
      if (stopTimerRef.current !== null) {
        window.clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (!recorder) {
        return;
      }
      recorderRef.current = null;
      recorder.onstop = () => {
        if (upload) {
          void uploadRecording();
        } else {
          chunksRef.current = [];
          setRecording(false);
        }
      };
      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else if (upload) {
        void uploadRecording();
      }
    },
    [uploadRecording],
  );

  useEffect(() => {
    return () => {
      stopRecorder(false);
    };
  }, [stopRecorder]);

  useEffect(() => {
    if (!recording) {
      return;
    }
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [recording]);

  if (!live) {
    return null;
  }

  const handleSaveShow = (): void => {
    requireAuth(() => {
      clearNotice();
      setSavingShow(true);
      void api
        .saveCollectionItem({ kind: 'SHOW', showId: show.id })
        .then(() => {
          setMessage(t('common.show.saveShowSaved'));
        })
        .catch(() => {
          setError(t('common.show.saveShowError'));
        })
        .finally(() => {
          setSavingShow(false);
        });
    }, 'generic');
  };

  const handleSnapshot = (): void => {
    requireAuth(() => {
      const runSnapshot = async (): Promise<void> => {
        const imageDataUrl = captureVideoJpeg(videoEl!);
        if (!imageDataUrl) {
          setError(t('common.show.snapshotNeedVideo'));
          return;
        }
        setSnapshotBusy(true);
        try {
          const result = await api.captureShowSnapshot(show.id, { imageDataUrl });
          onWallet(result.wallet);
          setMessage(t('common.show.snapshotSaved'));
        } catch (err) {
          if (!(err instanceof ApiError && err.code === 'INSUFFICIENT_BALANCE')) {
            setError(t('common.show.snapshotError'));
          }
        } finally {
          setSnapshotBusy(false);
        }
      };
      void (async () => {
        clearNotice();
        if (!remoteStream || !videoEl) {
          setError(t('common.show.snapshotNeedVideo'));
          return;
        }
        const ok = await requireSpendCoins(SHOW_SNAPSHOT_COINS, () => {
          void runSnapshot();
        });
        if (!ok) {
          return;
        }
        await runSnapshot();
      })();
    }, 'wallet');
  };

  const handleRecord = (): void => {
    requireAuth(() => {
      void (async () => {
        clearNotice();
        if (recording) {
          stopRecorder(true);
          setRecordingBusy(true);
          return;
        }
        if (!remoteStream) {
          setError(t('common.show.recordNeedVideo'));
          return;
        }
        const mime = pickRecorderMimeType();
        if (!mime) {
          setError(t('common.show.recordUnsupported'));
          return;
        }
        const runRecord = (): void => {
          try {
            const recorder = new MediaRecorder(remoteStream!, {
              mimeType: mime,
              videoBitsPerSecond: 250_000,
            });
            chunksRef.current = [];
            recorder.ondataavailable = (event) => {
              if (event.data.size > 0) {
                chunksRef.current.push(event.data);
              }
            };
            recorderRef.current = recorder;
            startedAtRef.current = Date.now();
            setElapsedMs(0);
            setRecording(true);
            recorder.start(1_000);
            stopTimerRef.current = window.setTimeout(() => {
              stopRecorder(true);
            }, RECORDING_MAX_MS);
          } catch {
            setError(t('common.show.recordUnsupported'));
          }
        };
        const ok = await requireSpendCoins(SHOW_PUBLIC_RECORDING_COINS, runRecord);
        if (!ok) {
          return;
        }
        runRecord();
      })();
    }, 'wallet');
  };

  const elapsedLabel = `${String(Math.floor(elapsedMs / 1000 / 60)).padStart(2, '0')}:${String(
    Math.floor(elapsedMs / 1000) % 60,
  ).padStart(2, '0')}`;

  const saveLabel = t('common.show.saveShow');
  const snapshotLabel = snapshotBusy
    ? t('common.show.snapshotBusy')
    : t('common.show.snapshot').replace('{{coins}}', String(SHOW_SNAPSHOT_COINS));
  const recordLabel = recordingBusy
    ? t('common.show.recordShowBusy')
    : recording
      ? t('common.show.recordShowStop').replace('{{time}}', elapsedLabel)
      : t('common.show.recordShow').replace('{{coins}}', String(SHOW_PUBLIC_RECORDING_COINS));
  const c2cLabel = c2cOn ? t('common.show.cam2camStop') : t('common.show.cam2camStart');
  const renderNotes = (): ReactElement => (
    <>
      {message ? <p className="show-room__capture-note">{message}</p> : null}
      {error ? <p className="show-room__capture-note show-room__capture-note--error">{error}</p> : null}
    </>
  );

  const strip = (
    <div className={`show-room__capture show-room__capture--strip${recording ? ' is-recording' : ''}`}>
      <div className="show-room__capture-row">
        <Button label={saveLabel} variant="ghost" disabled={savingShow} onClick={handleSaveShow} />
        {publicCapture ? (
          <>
            <Button
              label={snapshotLabel}
              variant="ghost"
              disabled={snapshotBusy || recordingBusy || recording}
              onClick={handleSnapshot}
            />
            <Button
              label={recordLabel}
              variant={recording ? 'primary' : 'ghost'}
              disabled={snapshotBusy || recordingBusy}
              onClick={handleRecord}
            />
          </>
        ) : null}
        {exclusiveC2c ? (
          <Button
            label={c2cLabel}
            variant={c2cOn ? 'primary' : 'ghost'}
            disabled={c2cBusy || (!inExclusive && !c2cOn)}
            onClick={() => {
              if (!inExclusive && !c2cOn) {
                setError(t('common.show.cam2camNeedJoin'));
                return;
              }
              onC2cToggle();
            }}
          />
        ) : null}
      </div>
      {renderNotes()}
    </div>
  );

  const menu = (
    <div
      className="show-room__more-capture-inner"
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
    >
      <button
        type="button"
        className="show-room__more-safety-btn"
        disabled={savingShow}
        onClick={handleSaveShow}
      >
        {saveLabel}
      </button>
      {publicCapture ? (
        <>
          <button
            type="button"
            className="show-room__more-safety-btn"
            disabled={snapshotBusy || recordingBusy || recording}
            onClick={handleSnapshot}
          >
            {snapshotLabel}
          </button>
          <button
            type="button"
            className={`show-room__more-safety-btn${recording ? ' show-room__more-safety-btn--active' : ''}`}
            disabled={snapshotBusy || recordingBusy}
            onClick={handleRecord}
          >
            {recordLabel}
          </button>
        </>
      ) : null}
      {exclusiveC2c ? (
        <button
          type="button"
          className={`show-room__more-safety-btn${c2cOn ? ' show-room__more-safety-btn--active' : ''}`}
          disabled={c2cBusy || (!inExclusive && !c2cOn)}
          onClick={() => {
            if (!inExclusive && !c2cOn) {
              setError(t('common.show.cam2camNeedJoin'));
              return;
            }
            onC2cToggle();
          }}
        >
          {c2cLabel}
        </button>
      ) : null}
      {renderNotes()}
    </div>
  );

  return (
    <>
      {strip}
      {menuSlot ? createPortal(menu, menuSlot) : null}
    </>
  );
}
